import Link from 'next/link';
import React, { useEffect, useState } from 'react';

import EyeIcon from '@app/components/icons/Eye';
import EyeSlashIcon from '@app/components/icons/EyeSlash';
import { NEXT_AUTH_METHOD } from '@app/constants/auth';
import { AuthState, useAuth } from '@app/hooks/useAuth';
import { cn } from '@app/libs/utils';
import { authService } from '@app/services/auth.service';

import { Button } from '../ui/Button';
import { PasswordInput } from '../ui/PasswordInput';
import { useToast } from '../ui/Toast';

interface LoginComponentProps {
  onLogin?: (...args: any[]) => void;
  className?: string;
}

export const LoginComponent: React.FC<LoginComponentProps> = props => {
  const toast = useToast();
  const { setIsAuth } = useAuth();
  const [loginDisable, setLoginDisable] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const validateForm = () => {
    if (email.trim() === '' || password.trim() === '') {
      setLoginDisable(true);
    } else {
      setLoginDisable(false);
    }
  };

  useEffect(() => {
    validateForm();
  }, [email, password]);

  const handleLogin = async () => {
    try {
      const { success, errors } = await authService.login(email, password);
      if (success) {
        setIsAuth(AuthState.AUTH);
        props.onLogin?.(true);
      } else {
        toast.error(errors?.message || 'Đăng nhập thất bại');
      }
    } catch {
      toast.error('Đăng nhập thất bại');
    }
  };

  return (
    <div className={cn('max-w-[90vw] w-160 p-5 flex flex-col gap-20', props.className)}>
      <div className="flex flex-col gap-4 items-center">
        <h2 className="text-3xl font-bold">Đăng nhập</h2>
        <p>
          Chưa có tài khoản?{' '}
          <Link href="/auth/register" className="underline hover:text-blue-800 transition-all">
            Đăng ký ngay
          </Link>
        </p>
      </div>

      <form
        className="flex flex-col gap-8"
        onSubmit={e => {
          e.preventDefault();
          handleLogin();
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex justify-between gap-2">
            <label htmlFor={NEXT_AUTH_METHOD === 'phone' ? 'phone' : 'email'}>
              {NEXT_AUTH_METHOD === 'phone' ? 'Số điện thoại' : 'Email'} của bạn
            </label>
          </div>
          <input
            type={NEXT_AUTH_METHOD === 'phone' ? 'tel' : 'email'}
            id={NEXT_AUTH_METHOD === 'phone' ? 'phone' : 'email'}
            name={NEXT_AUTH_METHOD === 'phone' ? 'phone' : 'email'}
            className="w-full border border-gray-300 rounded-xl py-2 px-5 h-14"
            autoComplete={NEXT_AUTH_METHOD === 'phone' ? 'tel' : 'email'}
            placeholder={`Nhập ${NEXT_AUTH_METHOD === 'phone' ? 'số điện thoại' : 'email'} của bạn`}
            onChange={e => {
              if (NEXT_AUTH_METHOD === 'phone') {
                setEmail(e.target.value.replace(/[^0-9]/g, ''));
              } else {
                setEmail(e.target.value);
              }
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <PasswordInput
            label="Mật khẩu"
            enableShowPassword
            forgotPassword
            onChange={setPassword}
            onEnter={handleLogin}
          />
        </div>
        <div className="flex justify-center mt-5">
          <Button
            type="button"
            className={cn(
              'w-full bg-blue-500 text-white rounded-xl py-2 px-5 h-14',
              loginDisable ? 'bg-gray-400 cursor-not-allowed' : 'cursor-pointer',
              'transition-all'
            )}
            disabled={loginDisable}
            onClick={handleLogin}
          >
            Đăng nhập
          </Button>
        </div>
      </form>
    </div>
  );
};
